import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, ShieldCheck, Building2 } from "lucide-react";

/**
 * Public verification page — no authentication required.
 * Anyone scanning the QR on a MIRO Digital ID card lands here.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function VerifyDigitalId() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/public/verify/${code}`);
        setData(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Verification failed");
      }
    })();
  }, [code]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4"
         data-testid="verify-page">
      <div className="max-w-md w-full bg-white border rounded-2xl shadow-md p-6">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="text-[var(--miro-navy)]" size={22}/>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">MIRO Consultancy</div>
            <div className="font-display font-bold text-lg">Digital ID verification</div>
          </div>
        </div>

        {!data && !error && <div className="text-sm text-slate-500">Verifying…</div>}

        {error && (
          <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        {data && !data.valid && (
          <div className="p-4 rounded-lg bg-red-50 border border-red-200" data-testid="verify-invalid">
            <div className="flex items-center gap-2 font-semibold text-red-700">
              <XCircle size={20}/> Not valid
            </div>
            <div className="text-sm mt-2 text-red-900">
              This code {data.reason === "not_found" ? "was not found" :
                data.reason === "revoked" ? "has been revoked" :
                data.reason === "suspended" ? "is currently suspended" :
                `is ${data.reason}`}.
            </div>
            <div className="mt-3 font-mono text-xs text-slate-500">{data.code}</div>
          </div>
        )}

        {data?.valid && (
          <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="verify-valid">
            <div className="flex items-center gap-2 font-semibold text-emerald-700">
              <CheckCircle2 size={20}/> Verified — active
            </div>
            <div className="mt-4 space-y-3">
              <Row label="Name" value={data.holder_name}/>
              <Row label="Role" value={data.holder_role?.replace(/_/g, " ")}/>
              <Row label="Type"
                   value={data.holder_type === "partner"
                     ? `${(data.partner_type || "").toUpperCase()} partner`
                     : "Employee"}/>
              <Row label="Organisation" value={<span className="inline-flex items-center gap-1"><Building2 size={14}/> {data.org}</span>}/>
              {data.issued_at && (
                <Row label="Issued" value={new Date(data.issued_at).toLocaleDateString()}/>
              )}
              <Row label="Identity code" value={<span className="font-mono">{data.code}</span>}/>
            </div>
          </div>
        )}

        <div className="mt-5 text-xs text-slate-500">
          Only non-sensitive fields are shown. This is a public verification endpoint.
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 w-28 shrink-0">{label}</div>
      <div className="text-sm font-semibold text-slate-800 break-words">{value}</div>
    </div>
  );
}
